import { useId } from 'react'
import { getIconProps, type IconProps } from '#types'

/**
 * Copy icon — two overlapping rounded squares.
 *
 * Used for copy-to-clipboard actions and duplicate operations.
 *
 * @example
 * ```tsx
 * <button>
 *   <CopyIcon className="h-4 w-4" /> Copy
 * </button>
 * ```
 */
export function CopyIcon({ strokeWidth = 2, ...props }: IconProps) {
  const maskId = useId()
  return (
    <svg {...getIconProps(props)} viewBox="0 0 24 24">
      <defs>
        <mask id={maskId}>
          {/* Full canvas visible, then cut out the front square's interior */}
          <rect width="24" height="24" fill="white" />
          <path
            d="M13.5 17C15.0212 17 16.1755 16.9174 17.0605 16.7207C17.9343 16.5265 18.4837 16.2351 18.8594 15.8594C19.2351 15.4837 19.5265 14.9343 19.7207 14.0605C19.9174 13.1755 20 12.0212 20 10.5C20 8.97878 19.9174 7.82455 19.7207 6.93945C19.5265 6.06571 19.2351 5.51631 18.8594 5.14062C18.4837 4.76494 17.9343 4.47348 17.0605 4.2793C16.1755 4.0826 15.0212 4 13.5 4C11.9788 4 10.8245 4.0826 9.93945 4.2793C9.06571 4.47348 8.51631 4.76494 8.14062 5.14062C7.76494 5.51631 7.47348 6.06571 7.2793 6.93945C7.0826 7.82455 7 8.97878 7 10.5C7 12.0212 7.0826 13.1755 7.2793 14.0605C7.47348 14.9343 7.76494 15.4837 8.14062 15.8594C8.51631 16.2351 9.06571 16.5265 9.93945 16.7207C10.8245 16.9174 11.9788 17 13.5 17Z"
            fill="black"
          />
        </mask>
      </defs>
      {/* Back square — masked so it doesn't bleed through the front square */}
      <path
        d="M10.5 20C12.0212 20 13.1755 19.9174 14.0605 19.7207C14.9343 19.5265 15.4837 19.2351 15.8594 18.8594C16.2351 18.4837 16.5265 17.9343 16.7207 17.0605C16.9174 16.1755 17 15.0212 17 13.5C17 11.9788 16.9174 10.8245 16.7207 9.93945C16.5265 9.06571 16.2351 8.51631 15.8594 8.14062C15.4837 7.76494 14.9343 7.47348 14.0605 7.2793C13.1755 7.0826 12.0212 7 10.5 7C8.97878 7 7.82455 7.0826 6.93945 7.2793C6.06571 7.47348 5.51631 7.76494 5.14062 8.14062C4.76494 8.51631 4.47348 9.06571 4.2793 9.93945C4.0826 10.8245 4 11.9788 4 13.5C4 15.0212 4.0826 16.1755 4.2793 17.0605C4.47348 17.9343 4.76494 18.4837 5.14062 18.8594C5.51631 19.2351 6.06571 19.5265 6.93945 19.7207C7.82455 19.9174 8.97878 20 10.5 20Z"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        mask={`url(#${maskId})`}
      />
      {/* Front square */}
      <path
        d="M13.5 17C15.0212 17 16.1755 16.9174 17.0605 16.7207C17.9343 16.5265 18.4837 16.2351 18.8594 15.8594C19.2351 15.4837 19.5265 14.9343 19.7207 14.0605C19.9174 13.1755 20 12.0212 20 10.5C20 8.97878 19.9174 7.82455 19.7207 6.93945C19.5265 6.06571 19.2351 5.51631 18.8594 5.14062C18.4837 4.76494 17.9343 4.47348 17.0605 4.2793C16.1755 4.0826 15.0212 4 13.5 4C11.9788 4 10.8245 4.0826 9.93945 4.2793C9.06571 4.47348 8.51631 4.76494 8.14062 5.14062C7.76494 5.51631 7.47348 6.06571 7.2793 6.93945C7.0826 7.82455 7 8.97878 7 10.5C7 12.0212 7.0826 13.1755 7.2793 14.0605C7.47348 14.9343 7.76494 15.4837 8.14062 15.8594C8.51631 16.2351 9.06571 16.5265 9.93945 16.7207C10.8245 16.9174 11.9788 17 13.5 17Z"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
